import normalizeUrl from "normalize-url";

import { FIRMWARE_URL, SERVER_URL, SIMULATOR_URL } from "~/utils/urls";

import { auth0Promise } from "~/utils/auth";
import { headersAndCredentials } from "~/utils/network";
import { createContainer } from "~/utils/unstated-next";

// The unprefixed keys are no longer read (see below). Drop them so localStorage
// does not carry dead entries around forever. This also retires the old
// hand-written checks for legacy values -- firmware.soulmatelights.com:8082/8083
// and editor.soulmatelights.com/sketches/build -- which are all covered by
// simply abandoning the keys.
delete localStorage.simulatorUrl;
delete localStorage.firmwareUrl;
delete localStorage.appServerUrl;

// These keys are deliberately not the old `simulatorUrl` / `firmwareUrl` /
// `appServerUrl`. The previous code persisted the current URL on every mount and
// then read it back as `localStorage.x || DEFAULT`, so the first page load on an
// origin pinned whatever the default was at the time and the build-time default
// became dead for that browser forever. New keys abandon those bad pins.
//
// It cannot happen again because nothing writes these on mount -- only the
// setters below, which are what Settings calls. So a stored value is always a
// deliberate choice, and changing a default reaches everyone who has not made
// one.
const SIMULATOR_KEY = "soulmate.simulatorUrl";
const FIRMWARE_KEY = "soulmate.firmwareUrl";
const APP_SERVER_KEY = "soulmate.appServerUrl";

let initialSimulatorUrl = localStorage[SIMULATOR_KEY] || SIMULATOR_URL;

let initialFirmwareUrl = localStorage[FIRMWARE_KEY] || FIRMWARE_URL;

let initialAppServerUrl = localStorage[APP_SERVER_KEY] || SERVER_URL;

function Config() {
  // TODO: Rename to simualtorUrl, firmwareUrl, appServerUrl
  const [simulator, setSimulatorState] = useState(initialSimulatorUrl);
  const [firmware, setFirmwareState] = useState(initialFirmwareUrl);
  const [appServer, setAppServerState] = useState(initialAppServerUrl);

  // Persist on deliberate change only. Doing this in a mount effect is what
  // pinned the defaults in the first place.
  const setSimulator = (url) => {
    localStorage[SIMULATOR_KEY] = url;
    setSimulatorState(url);
  };
  const setFirmware = (url) => {
    localStorage[FIRMWARE_KEY] = url;
    setFirmwareState(url);
  };
  const setAppServer = (url) => {
    localStorage[APP_SERVER_KEY] = url;
    setAppServerState(url);
  };

  const url = (path) => normalizeUrl(appServer + "/" + path);

  const post = async (path, body = {}) => {
    return fetch(url(path), {
      method: "post",
      ...(await headersAndCredentials()),
      body: JSON.stringify({ ...body }),
    }).then((d) => d.json());
  };

  const postDelete = async (path, body = {}) => {
    return fetch(url(path), {
      method: "delete",
      ...(await headersAndCredentials()),
      body: JSON.stringify({ ...body }),
    }).then((d) => d.json());
  };

  const put = async (path, body = {}) => {
    const auth = await auth0Promise;
    const authenticated = await auth.isAuthenticated();
    const headers = {};
    if (authenticated) headers["Authorization"] = await auth.getTokenSilently();
    return fetch(url(path), {
      method: "PUT",
      credentials: "include",
      body,
    }).then((d) => d.json());
  };

  const get = async (path, params) => {
    return fetch(url(path) + "?" + new URLSearchParams(params), {
      ...(await headersAndCredentials()),
    }).then((d) => d.json());
  };

  const postWithToken = async (path, params) => {
    return fetch(url(path), {
      method: "post",
      ...(await headersAndCredentials()),
      body: JSON.stringify({ ...params }),
    }).then((d) => d.json());
  };

  return {
    simulator,
    setSimulator,
    firmware,
    setFirmware,
    appServer,
    setAppServer,
    headersAndCredentials,
    post,
    postDelete,
    put,
    get,
    postWithToken,
    createContainer,
  };
}

export default createContainer(Config);
